/**
 * Cart state logger + ARIA live region announcements (Zenyth Pave / Dawn-derived cart).
 * Replicates the reference flow: extract → diff by key → persist → MutationObserver with delayed check.
 */
(function () {
  'use strict';

  const CART_PAGE_PATH_SUBSTRING = '/cart';

  const SELECTORS = {
    cartBubbleCount: '#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]',
    cartTotal: '#main-cart-footer .totals__total-value',
    itemRows: '#main-cart-items table.cart-items tbody tr.cart-item',
    title: '.cart-item__name',
    priceWrapper: '.cart-item__totals .cart-item__price-wrapper',
    variantDd: '.cart-item__details dl .product-option dd',
  };

  const CART_CONTAINER_SELECTOR = '#main-cart-items .js-contents';
  const CART_TABLE_SELECTOR = 'table.cart-items';
  const LIVE_REGION_ID = 'znt_maincart_liveregion_287367';
  const STORAGE_KEY = 'znt_cart_last_state';

  const INITIAL_DELAY_MS = 1000;
  const OBSERVER_CHECK_DELAY_MS = 2500;
  const LIVE_REGION_WRITE_DELAY_MS = 50;
  const LIVE_REGION_CLEAR_MS = 6000;

  console.log('Cart State Logger: Script loaded!');
  console.log('Current URL:', window.location.href);
  console.log('DOM ready state:', document.readyState);

  function whenDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function readQuantityForRow(row, lineNumber) {
    const input = row.querySelector('input.quantity__input[data-index]');
    if (input) {
      const v = parseInt(String(input.value).trim(), 10);
      return Number.isFinite(v) ? v : 0;
    }
    const fallback = row.querySelector('p.quantity__input.znt-quantity-fallback span[aria-hidden="true"]');
    if (fallback) {
      const v = parseInt(fallback.textContent.trim(), 10);
      return Number.isFinite(v) ? v : 0;
    }
    return 0;
  }

  function readLinePriceFromRow(row) {
    const wrap = row.querySelector(SELECTORS.priceWrapper);
    if (!wrap) return '';
    const saleDd = wrap.querySelector('.cart-item__discounted-prices dd.price.price--end');
    if (saleDd) return saleDd.textContent.trim();
    const direct = wrap.querySelector('.price.price--end:not(.cart-item__old-price)');
    if (direct) return direct.textContent.trim();
    const any = wrap.querySelector('.price.price--end');
    return any ? any.textContent.trim() : '';
  }

  function readVariantSummary(row) {
    const dds = row.querySelectorAll(SELECTORS.variantDd);
    if (!dds.length) return '';
    return Array.from(dds)
      .map((dd) => dd.textContent.trim())
      .filter(Boolean)
      .join(', ');
  }

  function logCartState() {
    const rows = document.querySelectorAll(SELECTORS.itemRows);
    const products = [];

    rows.forEach((row, index) => {
      const lineNumber = index + 1;
      const titleEl = row.querySelector(SELECTORS.title);
      const title = titleEl ? titleEl.textContent.trim() : '';
      const quantity = readQuantityForRow(row, lineNumber);
      const totalPrice = readLinePriceFromRow(row);
      const variant = readVariantSummary(row);
      const keyAttr = row.getAttribute('data-key');
      const key = keyAttr && keyAttr.length ? keyAttr : 'line-' + lineNumber;

      products.push({
        title,
        quantity,
        totalPrice,
        variant,
        lineNumber,
        key,
      });
    });

    let totalItems = 0;
    const bubbleEl = document.querySelector(SELECTORS.cartBubbleCount);
    if (bubbleEl) {
      const t = bubbleEl.textContent.trim();
      totalItems = parseInt(t, 10);
      if (!Number.isFinite(totalItems)) totalItems = 0;
    } else {
      totalItems = products.reduce((sum, p) => sum + (Number.isFinite(p.quantity) ? p.quantity : 0), 0);
    }

    let estimatedTotal = '';
    const totalEl = document.querySelector(SELECTORS.cartTotal);
    if (totalEl) {
      estimatedTotal = totalEl.textContent.trim();
    }

    return {
      totalItems,
      estimatedTotal,
      products,
    };
  }

  function diffStates(lastState, currentState) {
    const totalItemsChanged = lastState.totalItems !== currentState.totalItems;
    const estimatedTotalChanged = lastState.estimatedTotal !== currentState.estimatedTotal;

    const oldProducts = new Map(lastState.products.map((p) => [p.key, p]));
    const newProducts = new Map(currentState.products.map((p) => [p.key, p]));

    const productsAdded = [];
    const productsRemoved = [];
    const productsChanged = [];

    for (const [key, newProduct] of newProducts) {
      if (!oldProducts.has(key)) {
        productsAdded.push(newProduct);
        continue;
      }
      const oldProduct = oldProducts.get(key);
      const quantityChanged = oldProduct.quantity !== newProduct.quantity;
      const priceChanged = oldProduct.totalPrice !== newProduct.totalPrice;
      const titleChanged = oldProduct.title !== newProduct.title;
      if (quantityChanged || priceChanged || titleChanged) {
        productsChanged.push({ oldProduct, newProduct });
      }
    }

    for (const [key, oldProduct] of oldProducts) {
      if (!newProducts.has(key)) {
        productsRemoved.push(oldProduct);
      }
    }

    const hasChanges =
      totalItemsChanged ||
      estimatedTotalChanged ||
      productsAdded.length > 0 ||
      productsRemoved.length > 0 ||
      productsChanged.length > 0;

    return {
      hasChanges,
      totalItemsChanged,
      estimatedTotalChanged,
      productsAdded,
      productsRemoved,
      productsChanged,
    };
  }

  function buildAnnouncementParts(changes, currentState) {
    const announcementParts = [];

    changes.productsChanged.forEach(({ newProduct }) => {
      console.log(
        '[Cart announcement] product changed:',
        newProduct.key,
        newProduct.title,
        newProduct.quantity,
        newProduct.totalPrice
      );
      announcementParts.push(
        `${newProduct.title}: ${newProduct.quantity} items, Total price ${newProduct.totalPrice}`
      );
    });

    changes.productsAdded.forEach((product) => {
      announcementParts.push(`${product.title}: ${product.quantity} items, Total price ${product.totalPrice}`);
    });

    changes.productsRemoved.forEach((product) => {
      announcementParts.push(`${product.title}: removed`);
    });

    if (changes.totalItemsChanged) {
      announcementParts.push(`Total items in the cart: ${currentState.totalItems}`);
    }

    if (changes.estimatedTotalChanged) {
      announcementParts.push(`Estimated total ${currentState.estimatedTotal}`);
    }

    return announcementParts;
  }

  let lastState = null;
  let clearLiveRegionTimer = null;
  let lastAnnouncedMessage = '';

  function announceChanges(message) {
    const liveRegion = document.getElementById(LIVE_REGION_ID);
    if (!liveRegion) {
      console.warn('Cart announcement: live region not found (id: ' + LIVE_REGION_ID + ')');
      return;
    }

    liveRegion.setAttribute('aria-live', 'assertive');
    liveRegion.setAttribute('role', 'alert');
    liveRegion.setAttribute('aria-atomic', 'true');

    if (clearLiveRegionTimer) {
      clearTimeout(clearLiveRegionTimer);
      clearLiveRegionTimer = null;
    }

    let toSpeak = message;
    if (message === lastAnnouncedMessage) {
      toSpeak = '\u200B' + String(Date.now()) + '\u200B ' + message;
    }
    lastAnnouncedMessage = message;

    liveRegion.textContent = '';
    setTimeout(function () {
      liveRegion.textContent = toSpeak;
      console.log('Cart announcement (live region):', message);
      clearLiveRegionTimer = setTimeout(function () {
        liveRegion.textContent = '';
        clearLiveRegionTimer = null;
      }, LIVE_REGION_CLEAR_MS);
    }, LIVE_REGION_WRITE_DELAY_MS);
  }

  function saveStateToStorage(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('Cart announcement: could not write storage', e);
    }
  }

  function loadStateFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      console.warn('Cart announcement: could not read storage', e);
      return null;
    }
  }

  function extractCurrentState() {
    return logCartState();
  }

  function checkForChanges() {
    const currentState = extractCurrentState();

    if (lastState) {
      const changes = diffStates(lastState, currentState);
      if (changes.hasChanges) {
        const announcementParts = buildAnnouncementParts(changes, currentState);
        if (announcementParts.length > 0) {
          announceChanges(announcementParts.join('. '));
        }
      }
    }

    lastState = currentState;
    saveStateToStorage(currentState);
  }

  function setupMutationObserver() {
    const container = document.querySelector(CART_CONTAINER_SELECTOR);
    if (!container) {
      console.warn('Cart announcement: MutationObserver could not be set (container missing).');
      return;
    }

    const observer = new MutationObserver(function (mutations) {
      let shouldCheck = false;
      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];
        if (mutation.type === 'characterData') {
          shouldCheck = true;
          break;
        }
        if (mutation.type === 'childList') {
          const t = mutation.target;
          if (mutation.addedNodes.length > 0 || (mutation.removedNodes && mutation.removedNodes.length > 0)) {
            shouldCheck = true;
            break;
          }
          if (
            t &&
            t.classList &&
            (t.classList.contains('cart-items') ||
              (typeof t.querySelector === 'function' && t.querySelector(CART_TABLE_SELECTOR)))
          ) {
            shouldCheck = true;
            break;
          }
          if (t && t.closest && typeof t.closest === 'function' && t.closest(CART_TABLE_SELECTOR)) {
            shouldCheck = true;
            break;
          }
        }
      }
      if (shouldCheck) {
        setTimeout(function () {
          checkForChanges();
        }, OBSERVER_CHECK_DELAY_MS);
      }
    });

    observer.observe(container, { childList: true, subtree: true, characterData: true });
    console.log('Cart announcement: MutationObserver active on', CART_CONTAINER_SELECTOR);
  }

  /** Initializes in-memory baseline and wires the observer (same flow as reference script). */
  function setupChangeDetection() {
    setupMutationObserver();
  }

  function runInitialCartFlow() {
    if (!window.location.pathname.includes(CART_PAGE_PATH_SUBSTRING)) {
      return;
    }

    setTimeout(function () {
      const currentState = extractCurrentState();
      const storedState = loadStateFromStorage();

      if (storedState) {
        const changes = diffStates(storedState, currentState);
        if (changes.hasChanges) {
          const announcementParts = buildAnnouncementParts(changes, currentState);
          if (announcementParts.length > 0) {
            announceChanges(announcementParts.join('. '));
          }
        }
      }

      lastState = currentState;
      saveStateToStorage(currentState);

      setupChangeDetection();
    }, INITIAL_DELAY_MS);
  }

  whenDomReady(runInitialCartFlow);
})();
